import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService } from '@/lib/auth.service';
import { AuthUser } from '@/types/api.types';
import { useNavigate } from 'react-router-dom';
import { ROUTES, BYPASS_AUTH } from '@/lib/constants';
import { toast } from 'sonner';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    if (BYPASS_AUTH) {
      setUser({
        id: '999',
        email: 'dev@example.com',
        display_name: 'Development User',
        phone: '0501234567',
        role: 'admin',
        tenant_id: null,
        carrier_id: null,
      });
      setIsAuthenticated(true);
      setLoading(false);
      return true;
    }

    try {
      const profile = await authService.getProfile();
      setUser(profile);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      localStorage.removeItem('auth_user');
      setIsAuthenticated(false);
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname === ROUTES.LOGIN) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email: string, password: string) => {
    if (BYPASS_AUTH) {
      setUser({
        id: '999',
        email: email,
        display_name: 'Development User',
        phone: '0501234567',
        role: 'admin',
        tenant_id: null,
        carrier_id: null,
      });
      setIsAuthenticated(true);
      toast.success('تم تسجيل الدخول بنجاح (وضع التطوير)');
      navigate(ROUTES.DASHBOARD);
      return;
    }

    try {
      const response = await authService.login({ email, password });
      setUser(response);
      setIsAuthenticated(true);
      toast.success('تم تسجيل الدخول بنجاح');
      navigate(ROUTES.DASHBOARD);
    } catch (error: any) {
      const message: string =
        error?.response?.data?.message ||
        error?.message ||
        'Login failed. Please check your credentials.';

      if (message.toLowerCase().includes('verify your email')) {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }

      toast.error(message);
      throw error;
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    navigate(ROUTES.LOGIN);
    toast.success('تم تسجيل الخروج بنجاح');
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return ctx;
}
